import React from 'react';
import { createMuiTheme, MuiThemeProvider, responsiveFontSizes, makeStyles, ThemeOptions, Theme as MuiTheme } from '@material-ui/core/styles';
import { Breakpoints } from '@material-ui/core/styles/createBreakpoints';
import CssBaseline from '@material-ui/core/CssBaseline';
import _merge from 'lodash/merge';

import { useBrandingData } from './dataProviders';
import { findTheme, ThemeConfig, CssPropsAndValues } from './themes';


export interface Theme extends ThemeConfig {
  breakpoints: Breakpoints;
}

export function makeBtStyles( callbackOrStyles: ((theme: Theme) => object) | object, options: any = {} ) {
  if( typeof callbackOrStyles === 'function' ) {
    return makeStyles( (theme: MuiTheme) => {
      const themeConfig = theme as unknown as Theme;
      return callbackOrStyles(themeConfig);
    }, options) as (params?: any) => any;
  }
  else {
    return makeStyles(callbackOrStyles, options);
  }
}

const DEFAULT_FONT_FAMILY = [
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  'Oxygen',
  'Ubuntu',
  'Cantarell',
  '"Fira Sans"',
  '"Droid Sans"',
  '"Helvetica Neue"',
  'sans-serif',
];

const DEFAULT_THEME_CONFIG = {
  page: {
    fontFamily: DEFAULT_FONT_FAMILY.join(','),
    '-webkit-font-smoothing': 'antialiased',
    '-moz-osx-font-smoothing': 'grayscale',
  },
  link: {
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  }
};


const muiOverrides = (themeConfig: ThemeConfig) => ({
  overrides: {
    MuiCssBaseline: {
      '@global': {
        html: {
          height: '100%'
        }
      }
    },
    MuiButtonBase: {
      root: {
        color: (themeConfig.link && themeConfig.link.color) || (themeConfig.main && themeConfig.main.color)
      },
    },
    MuiButton: {
      root: {
        color: (themeConfig.link && themeConfig.link.color) || (themeConfig.main && themeConfig.main.color)
      },
    },
  }
});

const DEFAULT_BODY_BACKGROUND = '#222222';

const usePageStyles = makeStyles( (theme: ThemeConfig) => ({
  '@global': {
    'body': {
      position: 'relative',  // Make sure position:absolute elements land correctly, specifically PoweredBy
      minHeight: '100%',
      paddingBottom: '40px', // Make room for PoweredBy footer
      ...theme.page
    },
    'a': {
      ...theme.link,
    }
  },
}));


function PageStyles() {
  usePageStyles();
  return null;
}


interface ThemeConfigOverrides {
  page?: CssPropsAndValues;
  main?: CssPropsAndValues;
  link?: CssPropsAndValues;
  characteristicFont?: CssPropsAndValues;
  characteristicColor?: CssPropsAndValues;
  signatureRequired?: CssPropsAndValues;
}

export default function ThemeProvider({ children, configOverrides }: {
  children: React.ReactNode,
  configOverrides: ThemeConfigOverrides
}) {
  let theme;
  const { theme_key } = useBrandingData();
  let themeConfig = findTheme(theme_key);

  // Cascading defaults
  themeConfig = _merge(
    {},
    DEFAULT_THEME_CONFIG,
    themeConfig
  );

  // Hierarchical overrides
  themeConfig = {
    ...themeConfig,
    page: {
      color: DEFAULT_BODY_BACKGROUND,
      ...themeConfig.page
    },
    link: _merge( {}, themeConfig.main, themeConfig.link ),
    characteristicColor: _merge( {}, themeConfig.main, themeConfig.characteristicColor )
  };

  // Usage overrides (from layout) & page-level rules specially targetted in MUI
  themeConfig = _merge( {}, themeConfig, configOverrides );
  themeConfig = _merge( {}, muiOverrides(themeConfig), themeConfig );

  try {
    theme = createMuiTheme( themeConfig as ThemeOptions );
  }
  catch(_) {
    const emptyTheme: ThemeConfig = {
      page: {},
      main: {},
      link: {},
      characteristicFont: {},
      characteristicColor: {},
      signatureRequired: {},
      card: {},
      layoutSpecific: {
        layout01: { cardHeader: {}, recentTrackingDot: {} }
      }
    };

    theme = createMuiTheme( emptyTheme as ThemeOptions );
  }

  theme = responsiveFontSizes(theme);


  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline/>
      <PageStyles/>
      {children}
    </MuiThemeProvider>
  );
}
